import React from 'react'
import { Icon, StatusPill } from './ReportsShell'
import s from '../styles/Reports.module.scss'

const pointString = values => {
	const max = Math.max(...values, 1)
	const min = Math.min(...values, 0)
	const range = Math.max(max - min, 1)
	return values.map((value, index) => {
		const x = values.length === 1 ? 50 : (index / (values.length - 1)) * 100
		const y = 42 - ((value - min) / range) * 34
		return `${x},${y}`
	}).join(' ')
}

export const DecisionHero = ({ eyebrow, title, description, value, suffix, tone = 'purple', meta, action, visual, children }) => (
	<section className={`${s.decisionHero} ${s[`decisionHero--${tone}`] || ''}`}>
		<div className={s.decisionCopy}>
			<span className={s.decisionEyebrow}>{eyebrow}</span>
			<h2>{title}</h2>
			<p>{description}</p>
			{meta && <div className={s.decisionMeta}>{meta}</div>}
			{action}
		</div>
		{visual || (
			<div className={s.decisionNumber} aria-label={`${value}${suffix || ''}`}>
				<strong>{value}</strong><span>{suffix}</span>
			</div>
		)}
		{children}
	</section>
)

export const ReadinessRing = ({ value, label = 'report readiness' }) => (
	<div className={s.ringWrap}>
		<div
			className={s.readinessRing}
			style={{ '--ring-value': `${Math.max(0, Math.min(100, value)) * 3.6}deg` }}
			role="img"
			aria-label={`${value}% ${label}`}
		>
			<div><strong>{value}%</strong><span>{label}</span></div>
		</div>
	</div>
)

export const SparkLine = ({ values, labels = [], tone = 'purple', title, summary }) => (
	<div className={s.sparkChart} role="img" aria-label={`${title}: ${values.join(', ')}`}>
		<div className={s.chartTopline}>
			<div><strong>{title}</strong>{summary && <span>{summary}</span>}</div>
		</div>
		<svg viewBox="0 0 100 46" preserveAspectRatio="none" aria-hidden="true">
			<defs>
				<linearGradient id={`spark-${tone}`} x1="0" x2="0" y1="0" y2="1">
					<stop offset="0%" stopColor="currentColor" stopOpacity=".24" />
					<stop offset="100%" stopColor="currentColor" stopOpacity="0" />
				</linearGradient>
			</defs>
			<polygon points={`0,46 ${pointString(values)} 100,46`} fill={`url(#spark-${tone})`} />
			<polyline points={pointString(values)} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
		</svg>
		{labels.length > 0 && <div className={s.axisLabels}>{labels.map(label => <span key={label}>{label}</span>)}</div>}
	</div>
)

export const DistributionBar = ({ segments, label }) => {
	const total = segments.reduce((sum, item) => sum + item.value, 0) || 1
	return (
		<div className={s.distributionWrap} role="img" aria-label={`${label}: ${segments.map(item => `${item.label} ${item.value}`).join(', ')}`}>
			<div className={s.distributionBar}>
				{segments.map(item => (
					<div key={item.label} className={`${s.distributionSegment} ${s[`tone--${item.tone}`] || ''}`} style={{ width: `${(item.value / total) * 100}%` }} />
				))}
			</div>
			<div className={s.chartLegend}>
				{segments.map(item => <span key={item.label}><i className={s[`tone--${item.tone}`] || ''} />{item.label} <strong>{item.value}</strong></span>)}
			</div>
		</div>
	)
}

export const HorizontalBars = ({ rows, max, onSelect, selected, valueSuffix = '', ariaLabel }) => (
	<div className={s.horizontalBars} aria-label={ariaLabel}>
		{rows.map(row => {
			const content = <>
				<span className={s.horizontalLabel}>{row.label}</span>
				<span className={s.horizontalTrack}><span className={s[`tone--${row.tone || 'purple'}`]} style={{ width: `${Math.min(100, (row.value / Math.max(max || 1, 1)) * 100)}%` }} /></span>
				<strong>{row.value}{valueSuffix}</strong>
			</>
			return onSelect ? (
				<button key={row.id || row.label} type="button" className={selected === (row.id || row.label) ? s.horizontalRowActive : s.horizontalRow} onClick={() => onSelect(row)}>{content}</button>
			) : <div key={row.id || row.label} className={s.horizontalRow}>{content}</div>
		})}
	</div>
)

export const StackedActivityBars = ({ rows, keys, max, ariaLabel }) => (
	<div className={s.stackedRows} aria-label={ariaLabel}>
		{rows.map(row => {
			const total = keys.reduce((sum, key) => sum + Number(row[key.id] || 0), 0)
			return <div key={row.id} className={s.stackedRow}>
				<div className={s.stackedPerson}><strong>{row.name}</strong><span>{row.caseload} students · {row.studentsReached} reached</span></div>
				<div className={s.stackedTrack} title={`${row.name}: ${total} recorded activities`}>
					{keys.map(key => <span key={key.id} className={s[`tone--${key.tone}`]} style={{ width: `${(Number(row[key.id] || 0) / Math.max(max, 1)) * 100}%` }} />)}
				</div>
				<strong className={s.stackedTotal}>{total}</strong>
			</div>
		})}
		<div className={s.chartLegend}>{keys.map(key => <span key={key.id}><i className={s[`tone--${key.tone}`]} />{key.label}</span>)}</div>
	</div>
)

export const ActionQueue = ({ title = 'What needs attention', description, rows, emptyText = 'No actions need attention.' }) => (
	<section className={`${s.panel} ${s.actionPanel}`}>
		<div className={s.panelHeader}>
			<div><h2 className={s.panelTitle}>{title}</h2>{description && <p className={s.panelSubtitle}>{description}</p>}</div>
			<StatusPill tone={rows.length ? 'needs-action' : 'ready'}>{rows.length} open</StatusPill>
		</div>
		{rows.length ? <div className={s.actionList}>{rows.map((row, index) => (
			<div key={row.id || row.title} className={s.actionRow}>
				<span className={s.actionSignal}><Icon>{index === 0 ? 'priority_high' : 'schedule'}</Icon></span>
				<div><div className={s.actionTitleLine}><strong>{row.title}</strong>{row.status && <StatusPill tone={row.status === 'Priority' ? 'needs-action' : undefined}>{row.status}</StatusPill>}</div><span>{row.detail}</span>{row.owner && <small>{row.owner}</small>}</div>
			</div>
		))}</div> : <div className={s.emptyState}>{emptyText}</div>}
	</section>
)

export const SectionHeading = ({ eyebrow = 'Explanation', title, description, aside }) => (
	<div className={s.sectionHeading}>
		<div><span className={s.sectionKicker}>{eyebrow}</span><h2>{title}</h2>{description && <p>{description}</p>}</div>
		{aside}
	</div>
)

const STORY_ICONS = {
	win: 'check_circle',
	optimise: 'north_east',
	monitor: 'visibility',
}

export const StoryCards = ({ items }) => (
	<div className={s.storyGrid}>
		{items.map(item => (
			<article key={item.kind} className={`${s.storyCard} ${s[`storyCard--${item.kind}`] || ''}`}>
				<span className={s.storyIcon}><Icon>{STORY_ICONS[item.kind]}</Icon></span>
				<div className={s.storyCopy}>
					<span>{item.label}</span>
					<strong>{item.value}</strong>
					<p>{item.text}</p>
				</div>
			</article>
		))}
	</div>
)

const DONUT_COLORS = ['#7650b5', '#5686d9', '#49a37b', '#e49a45', '#d76573', '#8b74b7']

export const DonutChart = ({ rows, centerValue, centerLabel, ariaLabel }) => {
	const total = rows.reduce((sum, row) => sum + row.value, 0) || 1
	let cursor = 0
	const gradient = rows.map((row, index) => {
		const start = cursor
		cursor += (row.value / total) * 100
		return `${DONUT_COLORS[index % DONUT_COLORS.length]} ${start}% ${cursor}%`
	}).join(', ')
	return (
		<div className={s.donutLayout} role="img" aria-label={ariaLabel}>
			<div className={s.donut} style={{ background: `conic-gradient(${gradient || '#ebe7f1 0 100%'})` }}>
				<div><strong>{centerValue}</strong><span>{centerLabel}</span></div>
			</div>
			<div className={s.donutLegend}>{rows.map((row, index) => (
				<div key={row.label}><i style={{ background: DONUT_COLORS[index % DONUT_COLORS.length] }} /><span>{row.label}</span><strong>{row.value}</strong></div>
			))}</div>
		</div>
	)
}

export const ComparisonBars = ({ rows }) => {
	const max = Math.max(...rows.flatMap(row => [row.interest, row.exploring]), 1)
	return (
		<div className={s.comparisonRows}>
			{rows.map(row => <div className={s.comparisonRow} key={row.label}>
				<strong>{row.label}</strong>
				<div className={s.comparisonBarLine}><span>Interested</span><i><b style={{ width: `${(row.interest / max) * 100}%` }} /></i><em>{row.interest}</em></div>
				<div className={s.comparisonBarLine}><span>Exploring</span><i><b className={s.comparisonExploring} style={{ width: `${(row.exploring / max) * 100}%` }} /></i><em>{row.exploring}</em></div>
			</div>)}
		</div>
	)
}
